import type { ValidationRule } from '@/api/v2Client';

// Phase 5.X-validation-rules-tier-grouping —
// Pure helper that buckets a flat list of ValidationRule rows into
// per-Tier groups for the admin page's grouped display. V1's
// ValidationRulesScreen rendered rules by tier in a fixed canonical
// order; V2 is restoring that UX as a frontend-only refactor.
//
// The canonical order is the one Sharon expects on /admin/validation-rules:
// ICH Core first, then the FDA → EMA → MHRA → HC → PMDA → TGA → Swissmedic
// regional regulators, then Application Internal (Wave E.4/E.5/E.6 rules),
// then the catch-all "Agency Specific" bucket. Tier values not in the list
// are rendered after the canonical ones, sorted alphabetically — they don't
// crash the page, they just sort last.

export const TIER_ORDER = [
  'ICH Core',
  'FDA',
  'EMA',
  'MHRA',
  'Health Canada',
  'PMDA',
  'TGA',
  'Swissmedic',
  'Application Internal',
  'Agency Specific',
] as const;

/**
 * Validation Rules rows 3–5 (item 4) — the Agency filter's options. A FIXED
 * list, not one built from the loaded rows, so the filter stays the same when
 * the list is empty or filtered. Mirrors VALIDATION_RULE_AGENCIES in
 * src/lib/validation/catalogueTaxonomy.ts (a backend test fails on drift, as
 * it does for TIER_ORDER).
 */
export const AGENCY_OPTIONS = ['EMA', 'FDA', 'Health Canada', 'MHRA', 'PMDA', 'Swissmedic', 'TGA'] as const;

/**
 * A Tier value as it appears in `ValidationRule.Tier`. The seed migrations
 * only ever produce values from TIER_ORDER, but the column is typed `string`
 * server-side, so we accept any string and sort unknowns last.
 */
export type Tier = (typeof TIER_ORDER)[number] | string;

export interface TierGroup {
  readonly tier: Tier;
  readonly rules: ValidationRule[];
}

/**
 * Bucket a flat rule list into tier groups in canonical order.
 *
 * - Groups appear in TIER_ORDER first, then any unknown Tier values
 *   alphabetically last.
 * - Within each group, rules preserve their incoming order (so callers
 *   that sort upstream — e.g. by RuleId — keep that ordering).
 * - Tiers named in `alwaysShow` get a group even with zero rules
 *   (Validation Rules rows 3–5, item 1: the page passes every tier in the
 *   loaded catalogue, so filtering never removes a tier header — a filtered
 *   tier shows "0"). Other tiers with zero rules are left out.
 */
export function groupByTier(rules: ValidationRule[], alwaysShow: readonly Tier[] = []): TierGroup[] {
  // Build the per-tier bucket map preserving insertion order via Map.
  const buckets = new Map<Tier, ValidationRule[]>();
  for (const tier of alwaysShow) buckets.set(tier, []);
  for (const rule of rules) {
    const tier = rule.Tier;
    const existing = buckets.get(tier);
    if (existing) {
      existing.push(rule);
    } else {
      buckets.set(tier, [rule]);
    }
  }

  const canonicalIndex = new Map<string, number>(
    TIER_ORDER.map((t, i) => [t, i]),
  );

  // Split into canonical (known) vs unknown tiers.
  const knownGroups: TierGroup[] = [];
  const unknownGroups: TierGroup[] = [];
  for (const [tier, ruleList] of buckets) {
    const group: TierGroup = { tier, rules: ruleList };
    if (canonicalIndex.has(tier)) {
      knownGroups.push(group);
    } else {
      unknownGroups.push(group);
    }
  }

  // Sort known groups by canonical index; unknowns alphabetically.
  knownGroups.sort(
    (a, b) =>
      (canonicalIndex.get(a.tier) ?? Number.MAX_SAFE_INTEGER) -
      (canonicalIndex.get(b.tier) ?? Number.MAX_SAFE_INTEGER),
  );
  unknownGroups.sort((a, b) => a.tier.localeCompare(b.tier));

  return [...knownGroups, ...unknownGroups];
}
